import {
  BaseItem,
  CanHaveAnimation,
  CanHaveRotation,
  RenderMetaBaseItemCore,
} from '../shared';
import type { TextEffectConfig } from './effects/types';
import {
  AlignWord,
  CLIP_TYPES,
  ClipType,
} from '@/shared/apis/editor/clip-types';
import {
  EditorItemType,
  EditorStarterItem,
} from '@/features/remotion-editor/editor/items/item-type';
export type TextAlign = 'left' | 'center' | 'right';
export type TextDirection = 'ltr' | 'rtl';

/**
 * CJK 텍스트가 음절 단위로 잘리지 않도록 공백 기준으로만 줄바꿈한다.
 * overflowWrap: 'anywhere'는 단어 자체가 컨테이너보다 넓을 때 fallback.
 * 'break-word' 가 아닌 이유: break-word 는 min-content 내재 크기 계산에 끊김 지점을
 * 반영하지 않아, flex 자식 등 intrinsic 사이징 컨텍스트에서 무공백 초장문이 한 줄
 * 통짜 폭으로 늘어난다(deck flow-bake 측정이 캔버스를 뚫는 원인). 'anywhere' 는
 * min-content 에도 반영되어 박스가 컨테이너 폭에 갇히고, 명시 폭 박스에서의 실제
 * 줄바꿈 결과는 break-word 와 동일하다. bake-flow 측정 CSS 와 같은 값이어야 한다
 * (측정=렌더 일치 계약).
 */
export const WORD_BREAK_STYLE = {
  wordBreak: 'keep-all' as const,
  overflowWrap: 'anywhere' as const,
};

export type FontStyle = {
  variant: string;
  weight: string;
};

export type TextItemAlignWord = AlignWord & React.CSSProperties;

export type TextShadow = {
  color: string;
  opacity: number;
  visible: boolean;
  offsetX: number;
  offsetY: number;
  blur: number;
};

export const defaultTextShadow: TextShadow = {
  color: '#000000',
  opacity: 1,
  visible: false,
  offsetX: 10,
  offsetY: 10,
  blur: 10,
};

/**
 * 텍스트 배경 모드
 * - 'text-only': 텍스트가 있는 부분만 배경색 적용 (줄별로)
 * - 'full-area': 텍스트 영역 전체에 배경색 적용 (사각형)
 */
export type TextBackgroundMode = 'text-only' | 'full-area';

export type TextItemBackground = {
  color: string;
  opacity: number;
  visible: boolean;
  horizontalPadding: number;
  verticalPadding: number;
  borderRadius: number;
  mode: TextBackgroundMode;
  /**
   * 배경의 수직 위치 오프셋 (%)
   * 폰트마다 시각적 중앙이 다를 수 있어 미세 조정용
   * 양수: 배경이 위로 이동, 음수: 배경이 아래로 이동
   */
  verticalOffset: number;
};

export const defaultTextItemBackground: TextItemBackground = {
  color: '#000000',
  opacity: 1,
  visible: false,
  horizontalPadding: 40,
  verticalPadding: 8,
  borderRadius: 20,
  mode: 'text-only',
  verticalOffset: 5,
};

/**
 * 배경 렌더링에 필요한 최소 속성 인터페이스
 * TextItem과 CaptionsItem 모두 이 인터페이스를 사용하여 배경을 렌더링할 수 있습니다.
 */
export type BackgroundRenderableItem = {
  text: string;
  left: number;
  top: number;
  width: number;
  height: number;
  fontSize: number;
  fontFamily: string;
  fontStyle: FontStyle;
  lineHeight: number;
  letterSpacing: number;
  align: TextAlign;
  rotation: number;
  background?: TextItemBackground | null;
};

export type CaptionItemRenderMeta = {
  clipType: Extract<ClipType, 'caption'>;
  alignWords: TextItemAlignWord[];
  start: number;
  end: number;
  sceneId: number;
} & RenderMetaBaseItemCore;

export type TitleItemRenderMeta = {
  id: number;
  clipType: Extract<ClipType, 'title'>;
  title: string;
  selected: boolean;
  linkId: number | null | undefined;
  videoEnd: number | null | undefined;
  videoStart: number | null | undefined;
};

/**
 * 글자 단위 스타일링을 위한 텍스트 세그먼트
 * 모든 스타일 속성은 optional - 없으면 부모 TextItem 값을 상속
 */
export type TextSegment = {
  text: string;
  color?: string;
  strokeColor?: string;
  fontFamily?: string;
  fontWeight?: string;
  fontVariant?: string; // 'normal' | 'italic'
  fontSize?: number;
  opacity?: number; // 0 ~ 1
};

export type TextItem = BaseItem &
  CanHaveAnimation &
  CanHaveRotation & {
    type: Extract<EditorItemType, 'text'>;
    text: string;
    color: string;
    colorVisible?: boolean;
    align: TextAlign;
    fontFamily: string;
    fontStyle: FontStyle;
    fontSize: number;
    lineHeight: number;
    letterSpacing: number;
    whiteSpace?: string;
    overflow?: string;
    textOverflow?: string;
    resizeOnEdit: boolean;
    direction: TextDirection;
    strokeWidth: number;
    strokeColor: string;
    strokeVisible?: boolean;
    fadeInDurationInSeconds: number;
    fadeOutDurationInSeconds: number;
    shadow?: TextShadow;
    shadowVisible?: boolean;
    background?: TextItemBackground | null;
    backgroundVisible?: boolean;
    renderMeta?: CaptionItemRenderMeta | TitleItemRenderMeta;
    /**
     * 글자 단위 스타일링을 위한 세그먼트 배열
     * 있으면 Rich Text 모드, 없으면 기존 단일 스타일 모드 (하위 호환)
     */
    segments?: TextSegment[];
    /** 제목 온디맨드 번역본. title renderMeta 의 `title` 은 원문 보관본이다. */
    translatedTitle?: string;
    translatedLanguage?: string;
    /** 현재 표시 중인 제목 언어. 미지정은 원문으로 본다. */
    titleLanguage?: 'source' | string;
    /** 원문/번역 리치 텍스트를 전환 후에도 보존한다. */
    sourceTitleSegments?: TextSegment[];
    translatedTitleSegments?: TextSegment[];
    /**
     * 내용 기반 텍스트 이펙트(타자기 등). 판별 유니온 — 새 효과는 effects 레지스트리에
     * 멤버를 추가해 확장한다. 미지정 = 비활성(기존 동작). segments(rich text) 경로 미적용.
     * @see ./effects/registry.ts
     */
    effect?: TextEffectConfig;
  };

export const isTextItem = (item: EditorStarterItem): item is TextItem =>
  item.type === 'text';

export const isCaptionRenderMeta = (
  renderMeta: TextItem['renderMeta'],
): renderMeta is CaptionItemRenderMeta => {
  return renderMeta?.clipType === CLIP_TYPES.CAPTION;
};

export const isTitleRenderMeta = (
  renderMeta: TextItem['renderMeta'],
): renderMeta is TitleItemRenderMeta => {
  return renderMeta?.clipType === CLIP_TYPES.TITLE;
};

export const isCaptionTextItem = (
  item: TextItem,
): item is TextItem & { renderMeta: CaptionItemRenderMeta } => {
  return isCaptionRenderMeta(item.renderMeta);
};

export const isTitleTextItem = (
  item: TextItem,
): item is TextItem & { renderMeta: TitleItemRenderMeta } => {
  return isTitleRenderMeta(item.renderMeta);
};

export const isCaptionEditorStarterItem = (
  item?: EditorStarterItem,
): item is TextItem & { renderMeta: CaptionItemRenderMeta } => {
  if (!item) return false;

  return isTextItem(item) && isCaptionRenderMeta(item.renderMeta);
};
